// src/api/estadoPago.js - API para manejo de estados de pago
import { apiFetch } from './index.js';

/**
 * Obtener todos los estados de pago
 * @param {string} filtro - 'all', 'activos', 'inactivos' (si aplica)
 * @returns {Promise} Lista de estados de pago
 */
export const obtenerEstadosPago = async (filtro = 'all') => {
  try {
    console.log('📋 Obteniendo estados de pago...', { filtro });
    
    const endpoint = '/api/EstadoPago';
    const response = await apiFetch(endpoint);
    
    // Normalizar los datos para el frontend
    const data = Array.isArray(response) ? response : (response.data || []);
    const estadosNormalizados = data.map(estado => ({
      idEstadoPago: estado.idEstadoPago,
      nombre: estado.nombre,
      descripcion: estado.descripcion,
      color: estado.color,
      fechaCreacion: estado.fecha_creacion || estado.fechaCreacion,
      fecha_creacion: estado.fecha_creacion,
      totalPagos: estado.totalPagos || 0
    }));
    
    return {
      success: true,
      data: estadosNormalizados,
      count: estadosNormalizados.length,
      message: 'Estados de pago obtenidos exitosamente'
    };
    
  } catch (error) {
    console.error('❌ Error obteniendo estados de pago:', error);
    return {
      success: false,
      error: error.message,
      data: [],
      count: 0
    };
  }
};

/**
 * Obtener un estado de pago por ID
 * @param {number} id - ID del estado de pago
 * @returns {Promise} Datos del estado de pago
 */
export const obtenerEstadoPagoPorId = async (id) => {
  try {
    if (!id) {
      throw new Error('ID de estado de pago es requerido');
    }
    
    console.log(`🔍 Obteniendo estado de pago ID: ${id}`);
    
    const response = await apiFetch(`/api/EstadoPago/${id}`);
    
    const data = response.data || response;
    
    return {
      success: true,
      data: {
        idEstadoPago: data.idEstadoPago,
        nombre: data.nombre,
        descripcion: data.descripcion,
        color: data.color,
        fechaCreacion: data.fecha_creacion,
        fecha_creacion: data.fecha_creacion
      },
      message: response.message || 'Estado de pago obtenido exitosamente'
    };
    
  } catch (error) {
    console.error(`❌ Error obteniendo estado de pago ${id}:`, error);
    return {
      success: false,
      error: error.message,
      data: null
    };
  }
};

/**
 * Obtener estado de pago con relaciones
 * @param {number} id - ID del estado de pago
 * @returns {Promise} Datos del estado de pago con relaciones
 */
export const obtenerEstadoPagoConRelaciones = async (id) => {
  try {
    if (!id) {
      throw new Error('ID de estado de pago es requerido');
    }
    
    console.log(`🔍 Obteniendo estado de pago con relaciones ID: ${id}`);
    
    const response = await apiFetch(`/api/EstadoPago/${id}/conRelaciones`);
    
    return {
      success: true,
      data: response.data || response,
      message: response.message || 'Estado de pago obtenido exitosamente'
    };
    
  } catch (error) {
    console.error(`❌ Error obteniendo estado de pago con relaciones ${id}:`, error);
    return {
      success: false,
      error: error.message,
      data: null
    };
  }
};

/**
 * Verificar si un estado de pago existe por nombre
 * @param {string} nombre - Nombre a verificar
 * @returns {Promise} Resultado de la verificación
 */
export const verificarEstadoPagoPorNombre = async (nombre) => {
  try {
    if (!nombre || nombre.trim() === '') {
      throw new Error('Nombre es requerido');
    }
    
    console.log(`🔎 Verificando estado de pago por nombre: "${nombre}"`);
    
    const response = await apiFetch(`/api/EstadoPago/validar-nombre?nombre=${encodeURIComponent(nombre)}`);
    
    return {
      success: true,
      existe: response.exists || response.data?.exists || false,
      message: response.message || (response.exists ? 'El estado de pago ya existe' : 'Estado de pago disponible')
    };
    
  } catch (error) {
    console.error(`❌ Error verificando estado de pago "${nombre}":`, error);
    return {
      success: false,
      error: error.message,
      existe: false
    };
  }
};

/**
 * Verificar si un estado de pago existe por ID
 * @param {number} id - ID del estado de pago
 * @returns {Promise} Resultado de la verificación
 */
export const verificarEstadoPagoPorId = async (id) => {
  try {
    if (!id) {
      throw new Error('ID de estado de pago es requerido');
    }
    
    console.log(`🔎 Verificando estado de pago ID: ${id}`);
    
    const response = await apiFetch(`/api/EstadoPago/${id}/exists`);
    
    return {
      success: true,
      existe: response.exists || response.data?.exists || false,
      message: response.message || (response.exists ? 'El estado de pago existe' : 'Estado de pago no existe')
    };
    
  } catch (error) {
    console.error(`❌ Error verificando estado de pago ${id}:`, error);
    return {
      success: false,
      error: error.message,
      existe: false
    };
  }
};

/**
 * Crear un nuevo estado de pago
 * @param {Object} estadoData - Datos del estado de pago
 * @returns {Promise} Estado de pago creado
 */
export const crearEstadoPago = async (estadoData) => {
  try {
    // Validar datos requeridos
    if (!estadoData.nombre || estadoData.nombre.trim() === '') {
      throw new Error('El nombre del estado de pago es requerido');
    }
    
    console.log('➕ Creando nuevo estado de pago:', estadoData);
    
    const response = await apiFetch('/api/EstadoPago', {
      method: 'POST',
      body: JSON.stringify({
        nombre: estadoData.nombre.trim().toLowerCase(),
        descripcion: estadoData.descripcion || null,
        color: estadoData.color || 'secondary'
      })
    });
    
    return {
      success: true,
      data: response.data || response,
      message: response.message || 'Estado de pago creado exitosamente'
    };
    
  } catch (error) {
    console.error('❌ Error creando estado de pago:', error);
    return {
      success: false,
      error: error.message,
      data: null
    };
  }
};

/**
 * Actualizar un estado de pago existente
 * @param {number} id - ID del estado de pago
 * @param {Object} estadoData - Datos a actualizar
 * @returns {Promise} Estado de pago actualizado
 */
export const actualizarEstadoPago = async (id, estadoData) => {
  try {
    if (!id) {
      throw new Error('ID de estado de pago es requerido');
    }
    
    if (!estadoData.nombre || estadoData.nombre.trim() === '') {
      throw new Error('El nombre del estado de pago es requerido');
    }
    
    console.log(`✏️ Actualizando estado de pago ID: ${id}`, estadoData);
    
    const payload = {
      idEstadoPago: parseInt(id),
      nombre: estadoData.nombre.trim().toLowerCase(),
      descripcion: estadoData.descripcion || null,
      color: estadoData.color || 'secondary'
    };
    
    console.log('📦 Payload enviado a la API:', payload);
    
    const response = await apiFetch(`/api/EstadoPago/${id}`, {
      method: 'PUT',
      body: JSON.stringify(payload)
    });
    
    return {
      success: true,
      data: response.data || response,
      message: response.message || 'Estado de pago actualizado exitosamente'
    };
    
  } catch (error) {
    console.error(`❌ Error actualizando estado de pago ${id}:`, error);
    return {
      success: false,
      error: error.message,
      data: null
    };
  }
};

/**
 * Eliminar un estado de pago
 * @param {number} id - ID del estado de pago a eliminar
 * @returns {Promise} Resultado de la eliminación
 */
export const eliminarEstadoPago = async (id) => {
  try {
    if (!id) {
      throw new Error('ID de estado de pago es requerido');
    }
    
    console.log(`🗑️ Eliminando estado de pago ID: ${id}`);
    
    const response = await apiFetch(`/api/EstadoPago/${id}`, {
      method: 'DELETE'
    });
    
    return {
      success: true,
      data: response.data || response,
      message: response.message || 'Estado de pago eliminado exitosamente'
    };
    
  } catch (error) {
    console.error(`❌ Error eliminando estado de pago ${id}:`, error);
    return {
      success: false,
      error: error.message,
      data: null
    };
  }
};

// ==================== FUNCIONES AUXILIARES (CLIENTE-SIDE) ====================

/**
 * Buscar estados de pago por término (cliente-side)
 * @param {Array} estados - Lista de estados de pago
 * @param {string} termino - Término de búsqueda
 * @returns {Array} Estados filtrados
 */
export const buscarEstadosPagoLocal = (estados, termino) => {
  if (!termino || termino.trim() === '') {
    return estados;
  }
  
  const terminoLower = termino.toLowerCase();
  return estados.filter(estado => 
    estado.nombre?.toLowerCase().includes(terminoLower) ||
    estado.descripcion?.toLowerCase().includes(terminoLower) ||
    estado.color?.toLowerCase().includes(terminoLower)
  );
};

/**
 * Obtener estadísticas de estados de pago
 * @param {Array} estados - Lista de estados de pago
 * @returns {Object} Estadísticas
 */
export const obtenerEstadisticasEstadosPago = (estados) => {
  // Estados con descripción
  const conDescripcion = estados.filter(e => e.descripcion && e.descripcion.trim() !== '').length;
  
  // Distribución por colores
  const colores = {};
  estados.forEach(estado => {
    const color = estado.color || 'default';
    colores[color] = (colores[color] || 0) + 1;
  });
  
  // Estados recientes (últimos 7 días)
  const sieteDiasAtras = new Date();
  sieteDiasAtras.setDate(sieteDiasAtras.getDate() - 7);
  const recientes = estados.filter(e => {
    try {
      const fechaCreacion = new Date(e.fecha_creacion || e.fechaCreacion || 0);
      return fechaCreacion >= sieteDiasAtras;
    } catch {
      return false;
    }
  }).length;
  
  return {
    total: estados.length,
    conDescripcion,
    porcentajeConDescripcion: estados.length > 0 ? Math.round((conDescripcion / estados.length) * 100) : 0,
    colores,
    recientes,
    porcentajeRecientes: estados.length > 0 ? Math.round((recientes / estados.length) * 100) : 0
  };
};

/**
 * Ordenar estados de pago por diferentes criterios
 * @param {Array} estados - Lista de estados de pago
 * @param {string} criterio - Criterio de ordenamiento
 * @returns {Array} Estados ordenados
 */
export const ordenarEstadosPago = (estados, criterio = 'nombre') => {
  const estadosCopy = [...estados];
  
  switch (criterio) {
    case 'nombre':
      return estadosCopy.sort((a, b) => (a.nombre || '').localeCompare(b.nombre || ''));
    case 'nombre_desc':
      return estadosCopy.sort((a, b) => (b.nombre || '').localeCompare(a.nombre || ''));
    case 'fechaCreacion_desc':
      return estadosCopy.sort((a, b) => new Date(b.fecha_creacion || b.fechaCreacion || 0) - new Date(a.fecha_creacion || a.fechaCreacion || 0));
    case 'fechaCreacion':
      return estadosCopy.sort((a, b) => new Date(a.fecha_creacion || a.fechaCreacion || 0) - new Date(b.fecha_creacion || b.fechaCreacion || 0));
    case 'color':
      return estadosCopy.sort((a, b) => (a.color || '').localeCompare(b.color || ''));
    default:
      return estadosCopy;
  }
};

/**
 * Formatear fecha de estado de pago
 * @param {string} fechaISO - Fecha en formato ISO
 * @param {boolean} completo - Si incluir hora completa
 * @returns {string} Fecha formateada
 */
export const formatearFechaEstadoPago = (fechaISO, completo = false) => {
  if (!fechaISO) return 'N/A';
  
  try {
    const fecha = new Date(fechaISO);
    const opciones = {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    };
    
    if (completo) {
      opciones.hour = '2-digit';
      opciones.minute = '2-digit';
    }
    
    return fecha.toLocaleDateString('es-ES', opciones);
  } catch (error) {
    return fechaISO;
  }
};

/**
 * Obtener clase de color para Bootstrap
 * @param {string} color - Color del estado
 * @returns {string} Clase CSS de Bootstrap
 */
export const obtenerClaseColor = (color) => {
  const coloresMap = {
    'success': 'success',
    'warning': 'warning',
    'danger': 'danger',
    'info': 'info',
    'primary': 'primary',
    'secondary': 'secondary',
    'dark': 'dark',
    'light': 'light'
  };
  
  return coloresMap[color?.toLowerCase()] || 'secondary';
};

/**
 * Formatear descripción (cortar si es muy larga)
 * @param {string} descripcion - Descripción del estado
 * @param {number} maxLength - Longitud máxima
 * @returns {string} Descripción formateada
 */
export const formatearDescripcion = (descripcion, maxLength = 100) => {
  if (!descripcion) return 'Sin descripción';
  if (descripcion.length <= maxLength) return descripcion;
  return descripcion.substring(0, maxLength) + '...';
};

/**
 * Obtener etiqueta HTML para estado de pago
 * @param {Object} estado - Estado de pago
 * @returns {string} HTML de la etiqueta
 */
export const obtenerEtiquetaEstado = (estado) => {
  if (!estado) return '<span class="badge bg-secondary">N/A</span>';
  
  const colorClass = obtenerClaseColor(estado.color);
  const nombre = estado.nombre?.charAt(0).toUpperCase() + estado.nombre?.slice(1) || 'Sin nombre';
  
  return `<span class="badge bg-${colorClass}">${nombre}</span>`;
};

// ==================== FUNCIONES DE EXPORTACIÓN ====================

/**
 * Exportar estados de pago a Excel
 * @param {Array} estados - Lista de estados de pago
 * @param {string} filename - Nombre del archivo (sin extensión)
 * @returns {Object} Resultado de la exportación
 */
export const exportarExcel = (estados, filename = 'estados_pago') => {
  try {
    if (typeof window === 'undefined') {
      return {
        success: false,
        error: 'Exportación solo disponible en el navegador',
        filename: null
      };
    }
    
    const XLSX = require('xlsx');
    const wb = XLSX.utils.book_new();
    
    const data = estados.map(estado => ({
      'ID': estado.idEstadoPago || '',
      'Nombre': estado.nombre || '',
      'Descripción': estado.descripcion || '',
      'Color': estado.color || '',
      'Fecha Creación': formatearFechaEstadoPago(estado.fecha_creacion || estado.fechaCreacion, true)
    }));
    
    const ws = XLSX.utils.json_to_sheet(data);
    
    const wscols = [
      { wch: 8 },   // ID
      { wch: 20 },  // Nombre
      { wch: 50 },  // Descripción
      { wch: 12 },  // Color
      { wch: 20 }   // Fecha Creación
    ];
    ws['!cols'] = wscols;
    
    XLSX.utils.book_append_sheet(wb, ws, 'Estados de Pago');
    
    const fecha = new Date();
    const fechaStr = fecha.toISOString().split('T')[0];
    const nombreArchivo = `${filename}_${fechaStr}.xlsx`;
    
    XLSX.writeFile(wb, nombreArchivo);
    
    return {
      success: true,
      filename: nombreArchivo,
      message: `Archivo ${nombreArchivo} generado exitosamente`,
      count: estados.length
    };
    
  } catch (error) {
    console.error('❌ Error exportando a Excel:', error);
    return {
      success: false,
      error: error.message,
      filename: null
    };
  }
};

/**
 * Exportar estados de pago a PDF
 * @param {Array} estados - Lista de estados de pago
 * @param {string} titulo - Título del reporte
 * @param {string} logoBase64 - Logo en base64 (opcional)
 * @returns {Object} Resultado de la exportación
 */
export const exportarPDF = (estados, titulo = 'Reporte de Estados de Pago', logoBase64 = null) => {
  try {
    console.log('📊 Exportando PDF de estados de pago...');
    
    if (typeof window === 'undefined') {
      return {
        success: false,
        error: 'Exportación solo disponible en el navegador',
        filename: null
      };
    }
    
    if (!estados || estados.length === 0) {
      throw new Error('No hay estados de pago para exportar');
    }
    
    let jsPDF;
    try {
      const jspdf = require('jspdf');
      jsPDF = jspdf.jsPDF;
      require('jspdf-autotable');
    } catch (e) {
      console.error('❌ jsPDF no está disponible:', e);
      return {
        success: false,
        error: 'La librería jsPDF no está disponible',
        filename: null
      };
    }
    
    const doc = new jsPDF();
    const fecha = new Date();
    const fechaStr = fecha.toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
    
    let y = 20;
    const marginLeft = 20;
    const pageWidth = doc.internal.pageSize.width;
    
    let logoAgregado = false;
    if (logoBase64) {
      try {
        doc.addImage(logoBase64, 'JPEG', marginLeft, 10, 30, 15);
        logoAgregado = true;
        y = 35;
      } catch (logoError) {
        console.warn('⚠️ Error al agregar logo:', logoError);
        y = 20;
      }
    }
    
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    const titleX = logoAgregado ? 60 : marginLeft;
    doc.text('SYSTEM JJ', titleX, y);
    
    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    doc.text(titulo, titleX, y + 8);
    
    y += 20;
    doc.setFontSize(10);
    doc.text(`Fecha: ${fechaStr}`, marginLeft, y);
    doc.text(`Total: ${estados.length} estados`, marginLeft + 80, y);
    
    y += 15;
    const estadisticas = obtenerEstadisticasEstadosPago(estados);
    
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text('Resumen Estadístico', marginLeft, y);
    
    y += 8;
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    
    const resumen = [
      `Con descripción: ${estadisticas.conDescripcion} (${estadisticas.porcentajeConDescripcion}%)`,
      `Colores disponibles: ${Object.keys(estadisticas.colores).length}`,
      `Recientes (7 días): ${estadisticas.recientes}`
    ];
    
    resumen.forEach((linea, index) => {
      doc.text(`• ${linea}`, marginLeft + 5, y + (index * 5));
    });
    
    const tableData = estados.map(estado => [
      estado.idEstadoPago || '',
      estado.nombre || '',
      formatearDescripcion(estado.descripcion, 40) || '',
      estado.color || '',
      formatearFechaEstadoPago(estado.fecha_creacion || estado.fechaCreacion)
    ]);
    
    doc.autoTable({
      startY: y + 25,
      head: [['ID', 'Nombre', 'Descripción', 'Color', 'Fecha']],
      body: tableData,
      theme: 'striped',
      headStyles: {
        fillColor: [79, 70, 229],
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        fontSize: 9
      },
      columnStyles: {
        0: { cellWidth: 15 },
        1: { cellWidth: 35 },
        2: { cellWidth: 60 },
        3: { cellWidth: 20 },
        4: { cellWidth: 30 }
      },
      styles: {
        fontSize: 8,
        cellPadding: 2,
        overflow: 'linebreak'
      },
      didDrawPage: function(data) {
        doc.setFontSize(7);
        doc.setTextColor(100, 100, 100);
        doc.text('© SYSTEM JJ - Sistema de Gestión de Estados de Pago', marginLeft, 290);
        doc.text(`Página ${data.pageNumber}`, pageWidth - marginLeft, 290, null, null, 'right');
      }
    });
    
    const fechaISO = fecha.toISOString().split('T')[0];
    const nombreArchivo = `estados_pago_${fechaISO}.pdf`;
    doc.save(nombreArchivo);
    
    return {
      success: true,
      filename: nombreArchivo,
      message: 'PDF generado exitosamente',
      count: estados.length,
      withLogo: logoAgregado
    };
    
  } catch (error) {
    console.error('❌ Error exportando PDF:', error);
    return {
      success: false,
      error: error.message,
      filename: null
    };
  }
};

/**
 * Exportar reporte completo (Excel + PDF)
 * @param {Array} estados - Lista de estados de pago
 * @param {string} baseFilename - Nombre base del archivo
 * @param {string} logoBase64 - Logo en base64 (opcional)
 * @returns {Promise} Resultado de la exportación
 */
export const exportarReporteCompleto = async (estados, baseFilename = 'reporte_completo_estados_pago', logoBase64 = null) => {
  try {
    if (typeof window === 'undefined') {
      return {
        success: false,
        error: 'Exportación solo disponible en el navegador',
        filename: null
      };
    }
    
    if (estados.length === 0) {
      throw new Error('No hay datos para exportar');
    }
    
    const excelResult = exportarExcel(estados, baseFilename);
    if (!excelResult.success) {
      throw new Error(`Error en Excel: ${excelResult.error}`);
    }
    
    await new Promise(resolve => setTimeout(resolve, 500));
    
    const pdfResult = exportarPDF(estados, `Reporte de Estados de Pago - ${baseFilename}`, logoBase64);
    if (!pdfResult.success) {
      throw new Error(`Error en PDF: ${pdfResult.error}`);
    }
    
    return {
      success: true,
      excel: excelResult,
      pdf: pdfResult,
      message: 'Reporte completo generado exitosamente',
      count: estados.length
    };
    
  } catch (error) {
    console.error('❌ Error exportando reporte completo:', error);
    return {
      success: false,
      error: error.message,
      excel: null,
      pdf: null
    };
  }
};

// ==================== EXPORT DEFAULT ====================

export default {
  // Funciones API
  obtenerEstadosPago,
  obtenerEstadoPagoPorId,
  obtenerEstadoPagoConRelaciones,
  verificarEstadoPagoPorNombre,
  verificarEstadoPagoPorId,
  crearEstadoPago,
  actualizarEstadoPago,
  eliminarEstadoPago,
  
  // Funciones auxiliares
  formatearFechaEstadoPago,
  formatearDescripcion,
  buscarEstadosPagoLocal,
  obtenerEstadisticasEstadosPago,
  ordenarEstadosPago,
  obtenerClaseColor,
  obtenerEtiquetaEstado,
  
  // Funciones de exportación
  exportarExcel,
  exportarPDF,
  exportarReporteCompleto
};